import { Controller } from "@hotwired/stimulus"

const MAX_OUTPUT_DIMENSION = 2400

export default class extends Controller {
  static targets = [
    "cameraInput",
    "libraryInput",
    "fileArea",
    "fileName",
    "selectedPhoto",
    "selectedPreview",
    "selectedName",
    "changeButton",
    "noteField",
    "submitButton",
    "error",
    "modal",
    "stage",
    "preview",
    "zoom",
    "status",
    "confirmButton",
    "sourceSheet",
    "sheetCameraButton"
  ]

  connect() {
    this.activeInput = null
    this.pendingInput = null
    this.previousSelection = null
    this.sourceImage = null
    this.cropPreviewUrl = null
    this.selectionPreviewUrl = null
    this.offsetX = 0
    this.offsetY = 0
    this.scale = 1
    this.panStart = null
    this.cropReturnElement = null
    this.sourceSheetReturnElement = null
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.closeCrop({ restoreFocus: false })
    this.closeChangeSheet({ restoreFocus: false })
    this.revokeCropPreviewUrl()
    this.revokeSelectionPreviewUrl()
  }

  openCamera() {
    this.openSource(this.cameraInputTarget)
  }

  openLibrary() {
    this.openSource(this.libraryInputTarget)
  }

  prepareDirectLibrarySelection() {
    this.rememberPreviousSelection()
    this.pendingInput = this.libraryInputTarget

    // The mobile label is associated with a normal HTML file input. Clearing
    // only when switching inputs keeps the current photo safe if the native
    // picker is dismissed without choosing a new file.
    if (this.libraryInputTarget !== this.activeInput) this.libraryInputTarget.value = ""
  }

  openChangeSheet() {
    if (this.usesMobilePicker()) {
      this.prepareDirectLibrarySelection()
      this.libraryInputTarget.click()
      return
    }

    this.sourceSheetReturnElement = document.activeElement
    this.sourceSheetTarget.hidden = false
    this.sourceSheetTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("is-photo-source-sheet-open")
    document.addEventListener("keydown", this.handleKeydown)

    window.requestAnimationFrame(() => this.sheetCameraButtonTarget.focus())
  }

  closeChangeSheet({ restoreFocus = true } = {}) {
    if (!this.hasSourceSheetTarget) return

    this.sourceSheetTarget.hidden = true
    this.sourceSheetTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("is-photo-source-sheet-open")
    document.removeEventListener("keydown", this.handleKeydown)

    if (restoreFocus) this.sourceSheetReturnElement?.focus?.()
    this.sourceSheetReturnElement = null
  }

  chooseCamera() {
    this.closeChangeSheet({ restoreFocus: false })
    this.openCamera()
  }

  chooseLibrary() {
    this.closeChangeSheet({ restoreFocus: false })
    this.openLibrary()
  }

  cameraSelected() {
    const file = this.cameraInputTarget.files[0]
    if (!file) return

    this.commitSelection(this.cameraInputTarget, file, {
      status: "写真を選択しました。"
    })
  }

  async librarySelected() {
    const file = this.libraryInputTarget.files[0]
    if (!file) return

    this.clearError()
    this.showStatus("新しい写真の画角を調整しています。決定するまで現在の写真を保持します。")

    try {
      await this.openCrop(file)
    } catch (_error) {
      this.commitSelection(this.libraryInputTarget, file, {
        status: "この写真は画角調整を開けなかったため、そのまま送信します。"
      })
    }
  }

  validateSelection(event) {
    if (this.activeInput?.files?.length) {
      // Submit only the photo that the customer has explicitly confirmed as
      // the current one.
      this.activateInput(this.activeInput)
      this.clearSubmissionGuidance()
      return
    }

    event.preventDefault()
    this.showError("写真を1枚選んでください。")
  }

  zoomChanged(event) {
    this.scale = Number(event.target.value)
    this.clampOffset()
    this.renderCrop()
  }

  resetCrop() {
    this.scale = 1
    this.offsetX = 0
    this.offsetY = 0
    this.zoomTarget.value = "1"
    this.renderCrop()
  }

  startPan(event) {
    if (!this.sourceImage) return

    this.panStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      offsetX: this.offsetX,
      offsetY: this.offsetY
    }
    this.stageTarget.setPointerCapture?.(event.pointerId)
  }

  movePan(event) {
    if (!this.panStart || event.pointerId !== this.panStart.pointerId) return

    this.offsetX = this.panStart.offsetX + event.clientX - this.panStart.x
    this.offsetY = this.panStart.offsetY + event.clientY - this.panStart.y
    this.clampOffset()
    this.renderCrop()
  }

  endPan(event) {
    if (!this.panStart || event.pointerId !== this.panStart.pointerId) return

    this.stageTarget.releasePointerCapture?.(event.pointerId)
    this.panStart = null
  }

  async confirmCrop() {
    if (!this.sourceImage) return

    this.confirmButtonTarget.disabled = true
    const sourceFile = this.libraryInputTarget.files[0]

    try {
      const blob = await this.croppedBlob()
      const file = new File(
        [blob],
        `${sourceFile?.name?.replace(/\.[^.]+$/, "") || "photo"}.jpg`,
        { type: "image/jpeg", lastModified: Date.now() }
      )

      this.assignFile(this.libraryInputTarget, file)
      this.commitSelection(this.libraryInputTarget, file, {
        displayName: `${file.name}（画角調整済み）`,
        status: "画角を調整しました。"
      })
    } catch (_error) {
      if (sourceFile) {
        this.commitSelection(this.libraryInputTarget, sourceFile, {
          status: "画角調整を保存できなかったため、元の写真を送信します。"
        })
      } else {
        this.restorePreviousSelection()
        this.showError("写真の画角を保存できませんでした。もう一度お試しください。")
      }
    } finally {
      this.closeCrop({ restoreFocus: false })
      this.changeButtonTarget?.focus?.()
      if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false
    }
  }

  cancelCrop() {
    this.restorePreviousSelection()
    this.closeCrop({ restoreFocus: false })
    this.changeButtonTarget?.focus?.()
  }

  async openCrop(file) {
    this.revokeCropPreviewUrl()
    this.cropPreviewUrl = URL.createObjectURL(file)
    this.sourceImage = await this.loadImage(this.cropPreviewUrl)
    this.cropReturnElement = document.activeElement
    this.previewTarget.src = this.cropPreviewUrl
    this.modalTarget.hidden = false
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("is-photo-crop-open")
    document.addEventListener("keydown", this.handleKeydown)

    window.requestAnimationFrame(() => {
      this.configureStage()
      this.resetCrop()
      this.confirmButtonTarget.focus()
    })
  }

  closeCrop({ restoreFocus = true } = {}) {
    if (!this.hasModalTarget) return

    this.modalTarget.hidden = true
    this.modalTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("is-photo-crop-open")
    document.removeEventListener("keydown", this.handleKeydown)
    this.previewTarget?.removeAttribute("src")
    this.sourceImage = null
    this.panStart = null
    this.revokeCropPreviewUrl()

    if (restoreFocus) this.cropReturnElement?.focus?.()
    this.cropReturnElement = null
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return

    if (!this.sourceSheetTarget.hidden) {
      this.closeChangeSheet()
    } else if (!this.modalTarget.hidden) {
      this.cancelCrop()
    }
  }

  usesMobilePicker() {
    return !window.matchMedia("(hover: hover) and (pointer: fine)").matches
  }

  openSource(input) {
    this.rememberPreviousSelection()
    this.pendingInput = input

    // Do not clear the active input before the picker opens. On iOS, dismissing
    // a picker does not fire change; retaining it keeps the confirmed photo safe.
    if (input !== this.activeInput) input.value = ""
    input.click()
  }

  rememberPreviousSelection() {
    const file = this.activeInput?.files?.[0]
    this.previousSelection = file ? {
      input: this.activeInput,
      file,
      displayName: this.selectedNameTarget.textContent
    } : null
  }

  commitSelection(input, file, { displayName = file.name, status } = {}) {
    if (input.files[0] !== file) this.assignFile(input, file)
    this.activateInput(input)
    this.pendingInput = null
    this.previousSelection = null
    this.updateFileName(displayName)
    this.updateSelectedPhoto(file, displayName)
    this.clearError()
    this.showStatus(status)
    this.showSubmissionGuidance()
  }

  restorePreviousSelection() {
    const previous = this.previousSelection

    if (previous?.input && previous.file) {
      try {
        this.assignFile(previous.input, previous.file)
        this.activateInput(previous.input)
        this.updateFileName(previous.displayName || previous.file.name)
        this.updateSelectedPhoto(previous.file, previous.displayName || previous.file.name)
        this.showStatus("写真の変更を取り消しました。")
      } catch (_error) {
        this.activeInput = null
        this.clearSelectedPhoto()
        this.updateFileName()
        this.showError("元の写真に戻せませんでした。もう一度写真を選んでください。")
      }
    } else {
      if (this.pendingInput) this.pendingInput.value = ""
      this.activeInput = null
      this.clearSelectedPhoto()
      this.updateFileName()
      this.showStatus("")
    }

    this.pendingInput = null
    this.previousSelection = null
  }

  activateInput(input) {
    this.activeInput = input
    const inactiveInput = input === this.cameraInputTarget ? this.libraryInputTarget : this.cameraInputTarget
    inactiveInput.value = ""
  }

  assignFile(input, file) {
    const transfer = new DataTransfer()
    transfer.items.add(file)
    input.files = transfer.files
  }

  updateSelectedPhoto(file, displayName) {
    this.revokeSelectionPreviewUrl()
    this.selectionPreviewUrl = URL.createObjectURL(file)
    this.selectedPreviewTarget.src = this.selectionPreviewUrl
    this.selectedNameTarget.textContent = displayName
    this.selectedPhotoTarget.hidden = false
    this.fileAreaTarget.classList.add("is-selected")
  }

  clearSelectedPhoto() {
    this.selectedPhotoTarget.hidden = true
    this.selectedPreviewTarget.removeAttribute("src")
    this.selectedNameTarget.textContent = ""
    this.fileAreaTarget.classList.remove("is-selected")
    this.revokeSelectionPreviewUrl()
    this.clearSubmissionGuidance()
  }

  showSubmissionGuidance() {
    this.noteFieldTarget.classList.add("is-photo-submit-guidance")
    this.submitButtonTarget.classList.add("is-photo-submit-guidance")
  }

  clearSubmissionGuidance() {
    this.noteFieldTarget.classList.remove("is-photo-submit-guidance")
    this.submitButtonTarget.classList.remove("is-photo-submit-guidance")
  }

  updateFileName(name) {
    this.fileNameTarget.textContent = name ? `選択中：${name}` : "写真が選択されていません。"
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  showStatus(message) {
    this.statusTarget.textContent = message || ""
  }

  configureStage() {
    const aspectRatio = this.sourceImage.naturalWidth / this.sourceImage.naturalHeight
    const maxWidth = Math.min(window.innerWidth - 48, 420)
    const maxHeight = Math.min((window.visualViewport?.height || window.innerHeight) * 0.46, 420)
    let width = maxWidth
    let height = width / aspectRatio

    if (height > maxHeight) {
      height = maxHeight
      width = height * aspectRatio
    }

    this.stageTarget.style.width = `${Math.floor(width)}px`
    this.stageTarget.style.height = `${Math.floor(height)}px`
  }

  clampOffset() {
    const maxX = this.stageTarget.clientWidth * (this.scale - 1) / 2
    const maxY = this.stageTarget.clientHeight * (this.scale - 1) / 2
    this.offsetX = Math.min(maxX, Math.max(-maxX, this.offsetX))
    this.offsetY = Math.min(maxY, Math.max(-maxY, this.offsetY))
  }

  renderCrop() {
    this.previewTarget.style.transform = `translate(-50%, -50%) translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`
  }

  async croppedBlob() {
    const stageWidth = this.stageTarget.clientWidth
    const stageHeight = this.stageTarget.clientHeight
    const baseScale = stageWidth / this.sourceImage.naturalWidth
    const drawScale = baseScale * this.scale
    const sourceX = (stageWidth / 2 - this.offsetX - stageWidth * this.scale / 2) / drawScale
    const sourceY = (stageHeight / 2 - this.offsetY - stageHeight * this.scale / 2) / drawScale
    const sourceWidth = stageWidth / drawScale
    const sourceHeight = stageHeight / drawScale
    const outputScale = Math.min(1, MAX_OUTPUT_DIMENSION / Math.max(sourceWidth, sourceHeight))
    const canvas = document.createElement("canvas")

    canvas.width = Math.max(1, Math.round(sourceWidth * outputScale))
    canvas.height = Math.max(1, Math.round(sourceHeight * outputScale))

    canvas.getContext("2d").drawImage(
      this.sourceImage,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("画像を生成できませんでした")), "image/jpeg", 0.9)
    })
  }

  loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.decoding = "async"
      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = url
    })
  }

  revokeCropPreviewUrl() {
    if (!this.cropPreviewUrl) return

    URL.revokeObjectURL(this.cropPreviewUrl)
    this.cropPreviewUrl = null
  }

  revokeSelectionPreviewUrl() {
    if (!this.selectionPreviewUrl) return

    URL.revokeObjectURL(this.selectionPreviewUrl)
    this.selectionPreviewUrl = null
  }
}
